import { useEffect, useState } from 'react';

/**
 * The theming seam for ApexCharts. Apex paints from JS option objects at render
 * time, so CSS role tokens cannot reach inside the SVG — this hook reads the
 * resolved `--ds-*` custom properties off the root element and hands charts the
 * few paint values their options need, re-reading on every `data-theme` flip
 * (Apex will not follow a CSS variable live; the consuming chart must re-render
 * with new option values).
 *
 * Presentation only: no context, no payload, no fetch. The fallbacks are the
 * tokens' own published values per theme, used where custom properties do not
 * resolve (jsdom in tests, most importantly) so chart tests stay deterministic.
 */

export type ChartTheme = {
  /** 'light' | 'dark' — feeds Apex `tooltip.theme`. */
  mode: 'light' | 'dark';
  /** Axis label + legend ink (`--ds-ink-secondary`). */
  foreColor: string;
  /** Gridline colour (`--ds-edge`). */
  gridBorder: string;
  /** Y-axis title ink (`--ds-ink-tertiary` — the token nearest the old literal). */
  axisTitleColor: string;
};

const FALLBACKS: Record<ChartTheme['mode'], Omit<ChartTheme, 'mode'>> = {
  light: { foreColor: '#505866', gridBorder: '#dadbdd', axisTitleColor: '#6c778b' },
  dark: { foreColor: '#9fadbc', gridBorder: '#38414a', axisTitleColor: '#8c9bab' },
};

const readVar = (name: string, fallback: string): string => {
  const value = getComputedStyle(document.documentElement).getPropertyValue(name).trim();
  return value || fallback;
};

export const buildChartTheme = (): ChartTheme => {
  const mode: ChartTheme['mode'] =
    document.documentElement.getAttribute('data-theme') === 'dark' ? 'dark' : 'light';
  const fallback = FALLBACKS[mode];
  return {
    mode,
    foreColor: readVar('--ds-ink-secondary', fallback.foreColor),
    gridBorder: readVar('--ds-edge', fallback.gridBorder),
    axisTitleColor: readVar('--ds-ink-tertiary', fallback.axisTitleColor),
  };
};

const useChartTheme = (): ChartTheme => {
  const [theme, setTheme] = useState<ChartTheme>(buildChartTheme);

  useEffect(() => {
    const observer = new MutationObserver(() => setTheme(buildChartTheme()));
    observer.observe(document.documentElement, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return theme;
};

export default useChartTheme;
